import {TFunction} from 'i18next';
import {IActionListItem} from '../action-like-documents/ActionLikeDocuments';
import {ReportActionMenuOptionTypes} from '../../../shared/constants';

export type ActionType =
  | 'FOLDER'
  | 'TAG'
  | 'ASSIGNMENT'
  | 'QUICKASSIGNMENT'
  | 'RELEASE';

export type LikeModalOpenForType =
  | 'bulkAssign'
  | 'bulkTag'
  | 'bulkFolder'
  | 'quickBatch'
  | 'bulkRelease';
export interface ActionDataType {
  // ---- Properties for action data of Bulk Assign, Folder and Tag operation ----
  isAdd?: boolean;
  isNewAssignment?: boolean;
  isNew?: boolean;
  persistContent?: boolean;
  numberToAssign?: string;
  sampleMethod?: string;
  docCount?: number;
  masterDocListID?: number;
  assignmentId?: number; // when new assignment is created
  selectedAction?: ReportActionMenuOptionTypes;
  selectedSecurityGroupIds?: string[];
  selectedBulkReleaseAction?: string;
  // End ---- Properties for action data of Bulk Assign, Folder and Tag  operation ----
}

export const calculateFromPercentage = (total: number, percentage: number) => {
  // Convert percentage to value
  const result = total * (percentage / 100);

  // If result is less than 1 → return -1
  if (result < 1) {
    return -1;
  }

  // Get decimal part
  const decimal = result - Math.floor(result);

  // If decimal < 0.5 → round down
  // Else → round up
  if (decimal < 0.5) {
    return Math.floor(result);
  } else {
    return Math.ceil(result);
  }
};

export const getLikeActionMenuDefaultItems = (
  t: TFunction<['translation', ...string[]], undefined>
): IActionListItem[] => {
  return [
    {
      id: 'family_members',
      label: t(`common.actionMenu.familyMembers`),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'email_docs',
      label: t(`common.actionMenu.emailThread`),
      isLoading: false,
      count: 0,
      selected: false,
    },
    {
      id: 'near_duplicates',
      label: t(`common.actionMenu.nearDuplicates`),
      isLoading: false,
      count: 0,
      selected: false,
    },
  ];
};

export const getLikePopupActionType = (action: string) => {
  switch (action) {
    case 'bulkAssign':
      return 'ASSIGNMENT';
    case 'bulkTag':
      return 'TAG';
    case 'bulkFolder':
      return 'FOLDER';
    case 'quickBatch':
      return 'QUICKASSIGNMENT';
    case 'bulkRelease':
      return 'RELEASE';
    default:
      return undefined;
  }
};

export const getCommonExecuteActionPayload = (
  actionType: string,
  masterDocId: number,
  sourceDetails: {[key: string]: any} | undefined,
  sourceType: string,
  actionData: ActionDataType | null,
  selectedDestinationIds: string[],
  bulkSelectedAction: string,
  likeActionList?: any[],
  strSelections?: any
): {[key: string]: any} => {
  let destinationList: {[key: string]: any} = {
    SampleMethod: actionData?.sampleMethod,
    NumberToAssign: actionData?.numberToAssign,
    BulkActionTreeIDs:
      actionData?.selectedAction?.toLocaleLowerCase() === 'quickbatch'
        ? [actionData.assignmentId]
        : selectedDestinationIds,
    IsAdd: actionData?.isAdd,
    MasterDocListID: masterDocId,
  };

  const payload: {[key: string]: any} = {
    actionType: actionType,
    elementId: 'divBulkAction',
    selectedList: sourceDetails,
    sourceType:
      actionData?.selectedAction?.toLowerCase() === 'quickbatch' &&
      sourceType.toLowerCase() === 'doclist'
        ? 'DOCUMENT'
        : sourceType,
    masterDocListID: masterDocId,
    strSelections: strSelections ?? null,
  };

  switch (bulkSelectedAction) {
    case 'assign_documents': {
      payload.isAdd = actionData?.isAdd;
      destinationList = {
        ...destinationList,
        ActionType: 'BulkAssign',
        DestinationContextTypeCode: 'BULKASSIGN',
        CreateAssignment: false, // Hardcoded temporary
        AssignmentId: 0,
        IsNewAssignment: actionData?.isNew,
        IsPersistSearchHits: actionData?.persistContent,
      };
      break;
    }
    case 'unassign_documents': {
      destinationList = {
        ...destinationList,
        ActionType: 'BulkAssign',
        DestinationContextTypeCode: 'BULKASSIGN',
        CreateAssignment: false,
        AssignmentId: 0,
        SampleMethod: '',
        NumberToAssign: (actionData?.docCount ?? 0).toString(),
        IsAdd: false,
        IsNewAssignment: false,
        IsPersistSearchHits: false,
      };
      break;
    }
    case 'quickBatch': {
      payload.IsAdd = true;
      payload.IsQuick = true;
      destinationList = {
        ...destinationList,
        IsQuick: true,
        AssignmentId: 0,
        actionType: 'BulkAssign',
        IsPersistSearchHits: true,
        DestinationContextTypeCode: 'BULKASSIGN',
        CreateAssignment: false,
        IsNewAssignment: false,
      };
      break;
    }
    case 'bulkRelease': {
      payload.IsAdd = true;
      if (
        actionData?.selectedSecurityGroupIds &&
        actionData.selectedSecurityGroupIds.length > 0
      ) {
        destinationList.BulkActionTreeIDs =
          actionData.selectedSecurityGroupIds.map(Number);
      }
      delete destinationList.NumberToAssign;
      break;
    }
    case 'bulkUnrelease': {
      delete destinationList.NumberToAssign;
      delete payload.IsAdd;
      if (
        actionData?.selectedSecurityGroupIds &&
        actionData.selectedSecurityGroupIds.length > 0
      ) {
        destinationList.BulkActionTreeIDs =
          actionData.selectedSecurityGroupIds.map(Number);
      }
      break;
    }
    default:
      break;
  }
  const likeActionType = getLikeActionType(bulkSelectedAction) as ActionType;
  if (
    ['FOLDER', 'TAG', 'ASSIGNMENT', 'QUICKASSIGNMENT', 'RELEASE'].includes(
      likeActionType
    )
  ) {
    if (likeActionList && likeActionList.length > 0) {
      likeActionList.map((action) => {
        switch (action.id) {
          case 'family_members':
            destinationList.IncludeFamilyMemeber = action.selected;
            break;
          case 'email_docs':
            destinationList.IncludeEmailThreads = action.selected;
            break;
          case 'near_duplicates':
            destinationList.IncludeNearDuplicates = action.selected;
            break;
          default:
            break;
        }
      });
    }
  }
  payload.destinationList = destinationList;
  return payload;
};

export const getLikePopUpPayload = (
  actionType: string,
  masterDocId: number,
  sourceType: string,
  sourceDetails: {[key: string]: any} | undefined,
  aditionalData: {
    docAssign?: number;
    newFolderId?: number; // recevied only new folder is created and used in bulkFolder action
    newTagId?: number; // recevied only new tag is created and used in bulkTag action
  },
  actionData: ActionDataType | null,
  selectedDestinationIds: string[],
  selectedHitsDetails?: {[key: string]: any}
) => {
  const payload: {[key: string]: any} = {
    ActionType: actionType,
    ElementID: 'divBulkAction',
    SourceType: sourceType,
    SourceDetails: JSON.stringify(sourceDetails),
    DocAssign: (aditionalData.docAssign ?? 0).toString(),
    SelectedHitsDetails: selectedHitsDetails ?? null,
    MasterDocListID: masterDocId,
    IsAdd: actionData?.isAdd ?? false,
    SelectedDetinationIds: JSON.stringify({
      BulkActionTreeIDs: selectedDestinationIds.map(Number),
    }),
  };

  if (actionType === 'FOLDER' && aditionalData.newFolderId) {
    payload.SelectedDetinationIds = JSON.stringify({
      BulkActionTreeIDs: [aditionalData.newFolderId],
    });
  }

  if (actionType === 'TAG' && aditionalData.newTagId) {
    payload.SelectedDetinationIds = JSON.stringify({
      BulkActionTreeIDs: [aditionalData.newTagId],
    });
  }

  if (actionType === 'ASSIGN') {
    payload.IsPersistSearchHits = actionData?.persistContent ?? false;
  }

  if (actionType === 'QUICKASSIGNMENT') {
    payload.SelectedDetinationIds = JSON.stringify({
      BulkActionTreeIDs: [actionData?.assignmentId],
    });
    payload.IsPersistSearchHits = true;
    payload.IsQuick = true;
  }

  if (actionType === 'RELEASE') {
    payload.SelectedDetinationIds = JSON.stringify({
      BulkActionTreeIDs: selectedDestinationIds.map(Number),
    });
  }
  return payload;
};

export const getLikeActionType = (bulkSelectedAction: string) => {
  let actionType = '';
  switch (bulkSelectedAction) {
    case 'tagDocs':
      actionType = 'TAG';
      break;
    case 'folderDocs':
      actionType = 'FOLDER';
      break;
    case 'assign_documents':
      actionType = 'ASSIGNMENT';
      break;
    case 'quickBatch':
      actionType = 'QUICKASSIGNMENT';
      break;
    case 'bulkRelease':
    case 'bulkUnrelease':
      actionType = 'RELEASE';
      break;
  }
  return actionType;
};

export const getActionTypeFromMenuItem = (
  menuItemId: ReportActionMenuOptionTypes
): ActionType => {
  switch (menuItemId) {
    case ReportActionMenuOptionTypes.BulkTag:
      return 'TAG';
    case ReportActionMenuOptionTypes.BulkFolder:
      return 'FOLDER';
    case ReportActionMenuOptionTypes.BulkAssign:
    case ReportActionMenuOptionTypes.Unassign:
      return 'ASSIGNMENT';
    case ReportActionMenuOptionTypes.QuickBatch:
      return 'QUICKASSIGNMENT';
    case ReportActionMenuOptionTypes.BulkRelease:
      return 'RELEASE';
    default:
      return 'ASSIGNMENT';
  }
};
